import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import DocumentsTableDataRow from './DocumentsTableDataRow';

import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import {
  CASES_DOCUMENTS_LIST_TABLE_LABELS,
  DOCUMENTS_LIST_TABLE_LABELS,
} from '../../../constants/tableLabels';
import usePagination from '../../../hooks/usePagination';
import { ActiveSortField, DocumentData, FilterTypes } from '../../../types';
import { getDocumentsList } from '../../../api/requestsApi/documentsApi';
import TableFilters from '../../../components/TableFilters/TableFilters';
import { downloadCsvorExcel } from '../../../helpers/downloadCsv';
import TableActions from '../../../components/Table/TableActions';
import { DOCUMENT_REQUESTS_STATUS_FILTER_OPTIONS } from '../../../components/TableFilters/constants';
import useSnackBar from '../../../hooks/useSnackBar';
import { handleSortTableData } from '../../../helpers/handleSortTableData';

const DocumentsView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();

  const [documentsList, setDocumentsList] = useState<DocumentData[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    clientId: '',
    defendantId: '',
  });
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    plaintiff: {
      order: '',
      fieldName: 'plaintiff',
    },
    sentAt: {
      order: '',
      fieldName: 'sentAt',
    },
    completedAt: {
      order: '',
      fieldName: 'completedAt',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getDocumentsList({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setDocumentsList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v4/document-requests/export', 'POST', 'Documents_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      filters: { ...filters, ...(hasParams && params) },
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Document requests exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !documentsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={`/requests/create-document-request${
              params.caseId ? `/?caseId=${params.caseId}` : '/'
            }`}
            exportList={exportList}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              client
              timeFilter={'Request Date'}
              statusFilter={DOCUMENT_REQUESTS_STATUS_FILTER_OPTIONS}
              isStatusArray
            />
          )}
          {!isDataLoading && documentsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={
                params.caseId ? CASES_DOCUMENTS_LIST_TABLE_LABELS : DOCUMENTS_LIST_TABLE_LABELS
              }
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <DocumentsTableDataRow tableData={documentsList} />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default DocumentsView;
